import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BarController,
  BarElement,
  Chart,
  Legend,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { MatProgressBarModule } from '@angular/material/progress-bar';

Chart.register(BarElement, BarController, Tooltip, Legend);

type RawDataType = { x: number; y: number; tooltip: string };
interface TooltipType extends TooltipItem<'bar' | 'line'> {
  raw: RawDataType;
}
@Component({
  selector: 'thred-histogram',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule],
  templateUrl: './histogram.component.html',
  styleUrl: './histogram.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistogramComponent implements OnChanges {
  @Input({ required: true }) data: number[] = [];
  @Input() target: number | null = null;
  @Input() average: number | null = null;
  @Input() loading = signal(false);
  @ViewChild('histogram') set canvas(canvas: ElementRef) {
    if (canvas) {
      this.myCanvas.set(canvas);
      this.createChart(canvas);
    }
  }

  myCanvas = signal<ElementRef | null>(null);
  private chart: Chart | undefined;

  ngOnChanges(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    const canvas = this.myCanvas();
    if (canvas) {
      this.createChart(canvas);
    }
  }

  createChart(canvas: ElementRef<HTMLCanvasElement>) {
    const frequencyMap = new Map<number, number>();
    if (this.target !== null) {
      frequencyMap.set(this.target, 0);
      frequencyMap.set(-this.target, 0);
    }
    if (this.average !== null) {
      frequencyMap.set(this.average, 0);
      frequencyMap.set(-this.average, 0);
    }
    this.data.forEach((weight) => {
      const count = frequencyMap.get(weight) || 0;
      const oppositeCount = frequencyMap.get(-weight) || 0;
      if (oppositeCount === 0) {
        frequencyMap.set(-weight, 0);
      }
      frequencyMap.set(weight, count + 1);
    });
    const keys = Array.from(frequencyMap.keys());
    keys.sort((a, b) => a - b);
    const histogramData: RawDataType[] = [];
    for (const key of keys) {
      const value = frequencyMap.get(key);
      if (value !== undefined) {
        histogramData.push({
          x: key,
          y: value,
          tooltip: `${key}`,
        });
      }
    }

    const targetLineData = [];
    const averageLineData = [];

    const maxF = Math.max(...histogramData.map((d) => d.y));
    const maxFrequency = maxF ? maxF : 1;
    for (let y = 0; y <= maxFrequency; y += 0.1) {
      if (this.target !== null) {
        targetLineData.push({
          x: this.target,
          y,
          tooltip: `Target: ${this.target}`,
        });
      }
      if (this.average !== null) {
        averageLineData.push({
          x: this.average,
          y,
          tooltip: `Average: ${this.average}`,
        });
      }
    }
    this.chart = new Chart(canvas.nativeElement, {
      data: {
        labels: keys,
        datasets: [
          {
            type: 'line',
            label: 'Target',
            data: targetLineData,
            borderColor: '#ad1b1b',
            borderWidth: this.target === null ? 0 : 1.5,
            pointRadius: 0,
          },
          {
            type: 'line',
            label: 'Average',
            data: averageLineData,
            borderColor: '#10645c',
            borderWidth: this.average === null ? 0 : 1.5,
            pointRadius: 0,
          },
          {
            type: 'line',
            tension: 0.35,
            data: histogramData,
            label: 'Frequency',
            borderColor: 'black',
            borderWidth: 2,
            borderCapStyle: 'round',
            pointRadius: 0,
          },
          {
            type: 'bar',
            data: histogramData,
            label: 'Frequency',
            stack: 'a',
            backgroundColor: '#b0b0b0',
            hoverBackgroundColor: '#b0b0b0',
            hoverBorderWidth: 2,
            barPercentage: 0.95,
            categoryPercentage: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            callbacks: {
              title: function ([context]: TooltipType[]) {
                return context.raw.tooltip;
              },
            },
            displayColors: false,
          },

          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 20,
            },
          },
        },
        animation: {
          duration: 0,
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }
}
